// src/services/DatadogService/DatadogService.ts
"use client";

import { useEffect } from "react";
import { datadogRum } from "@datadog/browser-rum";
import { datadogLogs } from "@datadog/browser-logs";
import type { HttpErrorInfo } from "./types";
import { sanitizePayload } from "@/utils/sanitizePayload";
import { scheduleIdle, isClient } from "@/utils/browser";

/**
 * State tracking for Datadog SDK initialization.
 * Prevents double-init and allows defensive checks in logger functions.
 */
let isDatadogLogsReady = false;
let isDatadogRumReady = false;

/**
 * Initialize Datadog Logs SDK eagerly on module evaluation in the browser.
 * Must run as early as possible (at chunk evaluation time) to capture early
 * hydration errors and window.onerror before React effects flush.
 *
 * Performance impact is minimal compared to RUM DOM observers.
 */
export const initDatadogLogs = (): void => {
  if (!isClient()) return;
  if (isDatadogLogsReady) return; // Idempotent guard
  if (process.env.NEXT_PUBLIC_ENV !== "prod") return;

  try {
    datadogLogs.init({
      clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN || "",
      site: process.env.NEXT_PUBLIC_SITE || "datadoghq.eu",
      service: process.env.NEXT_PUBLIC_SERVICE || "qr-site",
      env: "production",
      version: process.env.NEXT_PUBLIC_RELEASE_VERSION,
      forwardErrorsToLogs: true,
      forwardConsoleLogs: ["error"],
      sessionSampleRate: 100,
    });
    isDatadogLogsReady = true;
  } catch (error) {
    console.error("[Datadog Logs Init Failed]", error);
  }
};

// Eager execution on client module load (captures initial hydration/window errors)
initDatadogLogs();

/**
 * Initialize Datadog RUM SDK after the page load event.
 * Deferred because RUM's DOM observers and workers compete with critical path (LCP).
 */
const initDatadogRum = (): void => {
  if (!isClient()) return;
  if (isDatadogRumReady) return; // Idempotent guard
  if (process.env.NEXT_PUBLIC_ENV !== "prod") return;

  try {
    datadogRum.init({
      applicationId: process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID,
      clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN,
      site: process.env.NEXT_PUBLIC_SITE,
      service: process.env.NEXT_PUBLIC_SERVICE,
      env: "production",
      version: process.env.NEXT_PUBLIC_RELEASE_VERSION,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 50,
      startSessionReplayRecordingManually: true,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: "mask-user-input",
    });
    isDatadogRumReady = true;

    scheduleIdle(() => {
      try {
        datadogRum.startSessionReplayRecording();
      } catch (error) {
        logWarning("[Session Replay Start Failed]", {
          error: error instanceof Error ? error.message : String(error),
        });
      }
    }, 3000);
  } catch (error) {
    console.error("[Datadog RUM Init Failed]", error);
  }
};

/**
 * Defer RUM initialization until after window.load event.
 * Ensures RUM does not block critical rendering path.
 */
export const deferRumInitToPageLoad = (): void => {
  if (!isClient()) return;

  if (document.readyState === "complete") {
    initDatadogRum();
    return;
  }

  const handleLoad = () => {
    initDatadogRum();
    window.removeEventListener("load", handleLoad);
  };

  window.addEventListener("load", handleLoad, { once: true });
};

/**
 * React component mounted in application layout.
 * Triggers deferred RUM initialization after client mount.
 */
export const DatadogInit = (): null => {
  useEffect(() => {
    deferRumInitToPageLoad();
  }, []);

  return null;
};

/**
 * Defensive check: is the Logs SDK ready to accept writes on the client?
 */
const isLogsReady = (): boolean => {
  return isClient() && isDatadogLogsReady && datadogLogs?.logger !== undefined;
};

export const logHttpError = (errorInfo: HttpErrorInfo): void => {
  if (process.env.NEXT_PUBLIC_ENV !== "prod") {
    console.error("[HTTP Error]", errorInfo);
    return;
  }

  if (!isLogsReady()) return;

  try {
    const {
      message,
      statusCode,
      endpoint,
      method = "UNKNOWN",
      payload,
      response,
      timestamp = new Date().toISOString(),
      userContext,
    } = errorInfo;

    datadogLogs.logger.error(message, {
      http: {
        status_code: statusCode,
        url: endpoint,
        method,
      },
      error: {
        message,
        timestamp,
      },
      request: {
        payload: sanitizePayload(payload),
      },
      response: {
        data: sanitizePayload(response),
      },
      user: sanitizePayload(userContext),
    });
  } catch (error) {
    console.error("[Datadog Logger Error]", error);
  }
};

export const logError = (error: Error, context?: Record<string, unknown>): void => {
  if (process.env.NEXT_PUBLIC_ENV !== "prod") {
    console.error("[App Error]", error, context);
    return;
  }

  if (!isLogsReady()) return;

  try {
    datadogLogs.logger.error(error.message, {
      error: {
        message: error.message,
        stack: error.stack,
        name: error.name,
      },
      context: sanitizePayload(context),
    });
  } catch (err) {
    console.error("[Datadog Logger Error]", err);
  }
};

export const logInfo = (message: string, context?: Record<string, unknown>): void => {
  if (process.env.NEXT_PUBLIC_ENV !== "prod") {
    console.info("[Info]", message, context);
    return;
  }

  if (!isLogsReady()) return;

  try {
    const sanitized = sanitizePayload(context);
    datadogLogs.logger.info(message, (sanitized as Record<string, unknown>) || {});
  } catch (error) {
    console.error("[Datadog Logger Error]", error);
  }
};

export const logEvent = (event: string, context?: Record<string, unknown>): void => {
  if (process.env.NEXT_PUBLIC_ENV !== "prod") {
    console.info("[Datadog Event]", event, { event, ...context });
    return;
  }

  if (!isLogsReady()) return;

  try {
    const sanitized = sanitizePayload(context) as Record<string, unknown> | undefined;
    datadogLogs.logger.info(event, {
      event,
      ...(sanitized || {}),
    });
  } catch (error) {
    console.error("[Datadog Logger Error]", error);
  }
};

export const logWarning = (message: string, context?: Record<string, unknown>): void => {
  if (process.env.NEXT_PUBLIC_ENV !== "prod") {
    console.warn("[Warning]", message, context);
    return;
  }

  if (!isLogsReady()) return;

  try {
    const sanitized = sanitizePayload(context);
    datadogLogs.logger.warn(message, (sanitized as Record<string, unknown>) || {});
  } catch (error) {
    console.error("[Datadog Logger Error]", error);
  }
};

/**
 * Test helper to reset internal module state between unit test runs.
 */
export const _resetDatadogStateForTesting = (): void => {
  isDatadogLogsReady = false;
  isDatadogRumReady = false;
};
